<script setup>
import { ref, computed, watch } from 'vue'
import { Edit2, Palette, FileText, Users, Tag, Settings } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'

const props = defineProps({
  open: {
    type: Boolean,
    default: false
  },
  features: {
    type: Array,
    default: () => []
  },
  projects: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['close', 'apply'])

// Form state
const formData = ref({
  // Name operations
  nameOperation: 'keep', // 'keep', 'replace', 'prefix', 'suffix'
  nameValue: '',

  // Description
  descriptionOperation: 'keep', // 'keep', 'replace', 'append'
  descriptionValue: '',

  // Color
  colorOperation: 'keep', // 'keep', 'replace'
  colorValue: '#3B82F6',

  // Visibility
  visibilityOperation: 'keep', // 'keep', 'public', 'private'

  // Project
  projectOperation: 'keep', // 'keep', 'assign'
  projectValue: '',

  // Static layer
  staticOperation: 'keep', // 'keep', 'enable', 'disable'
})

// Color presets
const colorPresets = [
  '#3B82F6', '#EF4444', '#10B981', '#F59E0B',
  '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'
]

// Computed properties
const selectedCount = computed(() => props.features.length)

const namePreview = computed(() => {
  if (!props.features.length) return ''

  const sample = props.features[0]
  const currentName = sample.properties.name || sample.properties.fileName || 'Unnamed'

  switch (formData.value.nameOperation) {
    case 'replace':
      return formData.value.nameValue || 'New Name'
    case 'prefix':
      return `${formData.value.nameValue}${currentName}`
    case 'suffix':
      return `${currentName}${formData.value.nameValue}`
    default:
      return currentName
  }
})

// Methods
const resetForm = () => {
  formData.value = {
    nameOperation: 'keep',
    nameValue: '',
    descriptionOperation: 'keep',
    descriptionValue: '',
    colorOperation: 'keep',
    colorValue: '#3B82F6',
    visibilityOperation: 'keep',
    projectOperation: 'keep',
    projectValue: '',
    staticOperation: 'keep'
  }
}

const handleClose = () => {
  resetForm()
  emit('close')
}

const handleApply = () => {
  const changes = {}

  // Build changes object based on form selections
  if (formData.value.nameOperation !== 'keep') {
    changes.nameOperation = formData.value.nameOperation
    changes.nameValue = formData.value.nameValue
  }

  if (formData.value.descriptionOperation !== 'keep') {
    changes.descriptionOperation = formData.value.descriptionOperation
    changes.descriptionValue = formData.value.descriptionValue
  }

  if (formData.value.colorOperation !== 'keep') {
    changes.colorValue = formData.value.colorValue
  }

  if (formData.value.visibilityOperation !== 'keep') {
    changes.visibilityValue = formData.value.visibilityOperation === 'public'
  }

  if (formData.value.projectOperation !== 'keep') {
    changes.projectValue = formData.value.projectValue
  }

  if (formData.value.staticOperation !== 'keep') {
    changes.staticValue = formData.value.staticOperation === 'enable'
  }

  emit('apply', changes)
  handleClose()
}

// Watch for dialog open/close
watch(() => props.open, (isOpen) => {
  if (isOpen) {
    resetForm()
  }
})
</script>

<template>
  <Dialog :open="open" @update:open="handleClose">
    <DialogContent class="max-w-2xl max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle class="flex items-center space-x-2">
          <Edit2 class="w-5 h-5" />
          <span>Bulk Edit Features</span>
        </DialogTitle>
        <p class="text-sm text-gray-600">
          Edit {{ selectedCount }} selected features at once
        </p>
      </DialogHeader>

      <div class="space-y-6">
        <!-- Name Section -->
        <div class="space-y-3">
          <div class="flex items-center space-x-2">
            <Tag class="w-4 h-4 text-gray-500" />
            <Label class="text-base font-medium">Name</Label>
          </div>

          <Select v-model="formData.nameOperation">
            <SelectTrigger>
              <SelectValue placeholder="Choose name operation" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="keep">Keep existing names</SelectItem>
              <SelectItem value="replace">Replace with new name</SelectItem>
              <SelectItem value="prefix">Add prefix</SelectItem>
              <SelectItem value="suffix">Add suffix</SelectItem>
            </SelectContent>
          </Select>

          <div v-if="formData.nameOperation !== 'keep'">
            <Input
              v-model="formData.nameValue"
              :placeholder="formData.nameOperation === 'replace' ? 'New name' : 'Text to add'"
            />
            <p class="text-xs text-gray-500 mt-1">
              Preview: <span class="font-medium">{{ namePreview }}</span>
            </p>
          </div>
        </div>

        <Separator />

        <!-- Description Section -->
        <div class="space-y-3">
          <div class="flex items-center space-x-2">
            <FileText class="w-4 h-4 text-gray-500" />
            <Label class="text-base font-medium">Description</Label>
          </div>

          <Select v-model="formData.descriptionOperation">
            <SelectTrigger>
              <SelectValue placeholder="Choose description operation" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="keep">Keep existing descriptions</SelectItem>
              <SelectItem value="replace">Replace with new description</SelectItem>
              <SelectItem value="append">Append to existing</SelectItem>
            </SelectContent>
          </Select>

          <div v-if="formData.descriptionOperation !== 'keep'">
            <Textarea
              v-model="formData.descriptionValue"
              :placeholder="formData.descriptionOperation === 'replace' ? 'New description' : 'Text to append'"
              rows="3"
            />
          </div>
        </div>

        <Separator />

        <!-- Color Section -->
        <div class="space-y-3">
          <div class="flex items-center space-x-2">
            <Palette class="w-4 h-4 text-gray-500" />
            <Label class="text-base font-medium">Color</Label>
          </div>

          <Select v-model="formData.colorOperation">
            <SelectTrigger>
              <SelectValue placeholder="Choose color operation" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="keep">Keep existing colors</SelectItem>
              <SelectItem value="replace">Set new color</SelectItem>
            </SelectContent>
          </Select>

          <div v-if="formData.colorOperation === 'replace'" class="space-y-2">
            <div class="flex items-center space-x-2">
              <input
                v-model="formData.colorValue"
                type="color"
                class="w-10 h-10 border rounded cursor-pointer"
              />
              <Input
                v-model="formData.colorValue"
                placeholder="#3B82F6"
                class="font-mono text-sm"
              />
            </div>
            <div class="flex items-center space-x-2">
              <span class="text-sm text-gray-600">Presets:</span>
              <div class="flex space-x-1">
                <button
                  v-for="color in colorPresets"
                  :key="color"
                  @click="formData.colorValue = color"
                  class="w-6 h-6 rounded border-2 border-gray-300 hover:border-gray-400"
                  :style="{ backgroundColor: color }"
                  :title="color"
                />
              </div>
            </div>
          </div>
        </div>

        <Separator />

        <!-- Visibility Section -->
        <div class="space-y-3">
          <div class="flex items-center space-x-2">
            <Users class="w-4 h-4 text-gray-500" />
            <Label class="text-base font-medium">Visibility</Label>
          </div>

          <Select v-model="formData.visibilityOperation">
            <SelectTrigger>
              <SelectValue placeholder="Choose visibility operation" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="keep">Keep existing visibility</SelectItem>
              <SelectItem value="public">Make public</SelectItem>
              <SelectItem value="private">Make private</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Separator />

        <!-- Project Section -->
        <div class="space-y-3" v-if="projects.length > 0">
          <div class="flex items-center space-x-2">
            <Settings class="w-4 h-4 text-gray-500" />
            <Label class="text-base font-medium">Project</Label>
          </div>

          <Select v-model="formData.projectOperation">
            <SelectTrigger>
              <SelectValue placeholder="Choose project operation" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="keep">Keep existing projects</SelectItem>
              <SelectItem value="assign">Assign to project</SelectItem>
            </SelectContent>
          </Select>

          <div v-if="formData.projectOperation === 'assign'">
            <Select v-model="formData.projectValue">
              <SelectTrigger>
                <SelectValue placeholder="Select project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  v-for="project in projects"
                  :key="project.project_id"
                  :value="project.project_id"
                >
                  {{ project.project_name }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Separator />

        <!-- Static Layer Section -->
        <div class="space-y-3">
          <div class="flex items-center space-x-2">
            <Settings class="w-4 h-4 text-gray-500" />
            <Label class="text-base font-medium">Static Layer</Label>
          </div>

          <Select v-model="formData.staticOperation">
            <SelectTrigger>
              <SelectValue placeholder="Choose static layer operation" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="keep">Keep existing settings</SelectItem>
              <SelectItem value="enable">Enable static layer</SelectItem>
              <SelectItem value="disable">Disable static layer</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" @click="handleClose">
          Cancel
        </Button>
        <Button @click="handleApply">
          Apply Changes
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
